/**
 * Enhanced Graph Node Components
 *
 * Custom React Flow nodes with:
 * - Hover tooltips with detailed info
 * - Type-based styling
 * - Selection states
 * - Connection handles
 * - Expand/collapse indicators
 */

import { memo, useState, useCallback } from 'react'
import { Handle, Position } from '@xyflow/react'
import {
  FileCode, Database, FileText, Box, Circle, Hexagon,
  ArrowRightCircle, ArrowLeftCircle, Phone, Layers,
  ChevronRight, ChevronDown, Info, Eye, ZoomIn
} from 'lucide-react'
import './GraphNodes.css'

// Node type configuration
const NODE_CONFIG = {
  program: {
    icon: Box,
    color: '#4a90d9',
    label: 'Program',
    description: 'Main program entry point',
  },
  copybook: {
    icon: FileText,
    color: '#6b8e23',
    label: 'Copybook',
    description: 'Included COBOL copybook',
  },
  file: {
    icon: Database,
    color: '#d4a574',
    label: 'File/Table',
    description: 'Data file or table access',
  },
  entry: {
    icon: ArrowRightCircle,
    color: '#e74c3c',
    label: 'Entry Point',
    description: 'Procedure entry point',
  },
  exit: {
    icon: ArrowLeftCircle,
    color: '#95a5a6',
    label: 'Exit Point',
    description: 'Procedure exit point',
  },
  paragraph: {
    icon: Layers,
    color: '#3498db',
    label: 'Paragraph',
    description: 'COBOL paragraph',
  },
  section: {
    icon: Hexagon,
    color: '#9b59b6',
    label: 'Section',
    description: 'COBOL section',
  },
  call: {
    icon: Phone,
    color: '#9b59b6',
    label: 'External Call',
    description: 'External program call',
  },
  default: {
    icon: Circle,
    color: '#8b949e',
    label: 'Node',
    description: 'Graph node',
  },
}

/**
 * Tooltip component for hover info
 */
function NodeTooltip({ data, config, visible }) {
  if (!visible) return null

  return (
    <div className="graph-node-tooltip">
      <div className="tooltip-header">
        <span className="tooltip-type" style={{ backgroundColor: config.color }}>
          {config.label}
        </span>
      </div>
      <div className="tooltip-content">
        <div className="tooltip-name">{data.label}</div>
        {data.sublabel && (
          <div className="tooltip-sublabel">{data.sublabel}</div>
        )}
        {data.startLine && (
          <div className="tooltip-detail">
            <span>Lines:</span> {data.startLine} - {data.endLine}
          </div>
        )}
        {data.statementCount > 0 && (
          <div className="tooltip-detail">
            <span>Statements:</span> {data.statementCount}
          </div>
        )}
        {data.clusterLabel && (
          <div className="tooltip-detail">
            <span>Group:</span> {data.clusterLabel}
          </div>
        )}
      </div>
      <div className="tooltip-footer">
        <Info size={12} /> Click for details
      </div>
    </div>
  )
}

/**
 * Main dependency node component
 */
export const DependencyNode = memo(({ data, selected, id }) => {
  const [isHovered, setIsHovered] = useState(false)

  const nodeType = data.nodeType || 'default'
  const config = NODE_CONFIG[nodeType] || NODE_CONFIG.default
  const Icon = config.icon

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  // Determine if this node has children (for expand indicator)
  const hasChildren = data.hasChildren || false
  const isExpanded = data.isExpanded !== false

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        className="graph-handle target-handle"
      />

      <div
        className={`graph-node ${nodeType} ${selected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
        style={{
          '--node-color': data.fillColor || config.color,
          '--node-border': selected ? '#fff' : (data.fillColor || config.color),
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="node-icon">
          <Icon size={14} />
        </div>
        <div className="node-content">
          <div className="node-label">{data.label}</div>
          {data.sublabel && (
            <div className="node-sublabel">{data.sublabel}</div>
          )}
        </div>
        {hasChildren && (
          <div className="node-expand">
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </div>
        )}

        {/* Hover tooltip */}
        <NodeTooltip data={data} config={config} visible={isHovered} />
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="graph-handle source-handle"
      />
    </>
  )
})

DependencyNode.displayName = 'DependencyNode'

/**
 * Compact node for detailed views with many nodes
 */
export const CompactNode = memo(({ data, selected }) => {
  const [isHovered, setIsHovered] = useState(false)

  const nodeType = data.nodeType || 'default'
  const config = NODE_CONFIG[nodeType] || NODE_CONFIG.default

  return (
    <>
      <Handle type="target" position={Position.Top} className="graph-handle compact" />

      <div
        className={`graph-node compact ${nodeType} ${selected ? 'selected' : ''}`}
        style={{ '--node-color': data.fillColor || config.color }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className="compact-label">{data.label}</span>
        <NodeTooltip data={data} config={config} visible={isHovered} />
      </div>

      <Handle type="source" position={Position.Bottom} className="graph-handle compact" />
    </>
  )
})

CompactNode.displayName = 'CompactNode'

/**
 * Group/Cluster node for containing related nodes
 */
export const GroupNode = memo(({ data }) => {
  const borderColor = data.color || '#30363d'

  return (
    <div
      className="graph-group-node"
      style={{ '--group-color': borderColor }}
    >
      <div className="group-header">{data.label}</div>
    </div>
  )
})

GroupNode.displayName = 'GroupNode'

/**
 * Node type registry for React Flow
 */
export const nodeTypes = {
  dependency: DependencyNode,
  compact: CompactNode,
  group: GroupNode,
}

export default nodeTypes
